export type FormVisibility = 'Public' | 'Hidden';
export type FormStatus = 'Draft' | 'Published';
export type ConditionType = 'LastPurchase' | 'StoppedBuying' | 'FirstPurchase' | 'LastVisit' | 'VisitFrequency' | 'NeverPurchased' | 'NeverVisited';
export type ConditionOperator = 'MoreThan' | 'LessThan' | 'Exactly' | 'AtMost' | 'Every';
export type ConditionPeriod = 'Days' | 'Weeks' | 'Months';
export type ConditionConnector = 'And' | 'Or';

export interface DynamicFormListItem {
  id: number;
  title: string;
  visibility: FormVisibility;
  status: FormStatus;
  conditionCount: number;
  questionCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface DynamicFormDetails {
  id: number;
  title: string;
  visibility: FormVisibility;
  status: FormStatus;
  alwaysDisplay: boolean;
  createdAt: string;
  updatedAt: string;
  publishedAt: string | null;
  conditionGroups: ConditionGroupDto[];
  sections: SectionDto[];
}

export interface ConditionGroupDto {
  id: number;
  connector: ConditionConnector;
  order: number;
  conditions: ConditionDto[];
}

export interface ConditionDto {
  id: number;
  conditionType: ConditionType;
  operator: ConditionOperator | null;
  timeValue: number | null;
  period: ConditionPeriod | null;
  order: number;
}

export interface SectionDto {
  id: number;
  title: string;
  order: number;
  questions: QuestionDto[];
}

export interface QuestionDto {
  id: number;
  text: string;
  order: number;
}

export interface DynamicFormFilters {
  search?: string;
  page: number;
  pageSize: number;
}

// Create/Update request types
export interface CreateDynamicFormRequest {
  title: string;
  visibility: FormVisibility;
  alwaysDisplay: boolean;
  conditionGroups: CreateConditionGroupRequest[];
  sections: CreateSectionRequest[];
}

export interface CreateConditionGroupRequest {
  connector: ConditionConnector;
  order: number;
  conditions: CreateConditionRequest[];
}

export interface CreateConditionRequest {
  conditionType: ConditionType;
  operator: ConditionOperator | null;
  timeValue: number | null;
  period: ConditionPeriod | null;
  order: number;
}

export interface CreateSectionRequest {
  title: string;
  order: number;
  questions: CreateQuestionRequest[];
}

export interface CreateQuestionRequest {
  text: string;
  order: number;
}

// Helper functions for display text
export function getVisibilityLabel(v: FormVisibility): string { 
  return v === 'Public' ? 'Público' : 'Oculto'; 
}

export function getStatusLabel(s: FormStatus): string { 
  return s === 'Draft' ? 'Rascunho' : 'Publicado'; 
}

export function getConditionTypeLabel(t: ConditionType): string {
  switch (t) {
    case 'LastPurchase': return 'Última compra foi a';
    case 'StoppedBuying': return 'Parou de comprar a';
    case 'FirstPurchase': return 'Comprou pela primeira vez a';
    case 'LastVisit': return 'Última visita foi a';
    case 'VisitFrequency': return 'Frequência de visita é';
    case 'NeverPurchased': return 'Nunca comprou';
    case 'NeverVisited': return 'Nunca foi visitado';
    default: return t;
  }
}

export function getConditionOperatorLabel(o: ConditionOperator): string {
  switch (o) {
    case 'MoreThan': return 'Mais de';
    case 'LessThan': return 'Menos de';
    case 'Exactly': return 'Exatamente';
    case 'AtMost': return 'No máximo';
    case 'Every': return 'A cada';
    default: return o;
  }
}

export function getConditionPeriodLabel(p: ConditionPeriod): string {
  switch (p) {
    case 'Days': return 'Dias';
    case 'Weeks': return 'Semanas';
    case 'Months': return 'Meses';
    default: return p;
  }
}
